import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAuth } from "@/App";
import { 
  Shield, Building2, Users, Key, BarChart3, Megaphone, 
  Trash2, Ban, CheckCircle, Power, Edit, Plus, ArrowLeft,
  Calendar, AlertTriangle, RefreshCw, Pencil, Crown, UserCog, LogIn,
  Package, Tag, Settings2
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SuperAdminPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("stats");
  const [impersonating, setImpersonating] = useState(false);

  // Data states
  const [stats, setStats] = useState(null);
  const [organizations, setOrganizations] = useState([]);
  const [users, setUsers] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [banner, setBanner] = useState({ message: "", is_active: false, bg_color: "#FEF3C7", text_color: "#92400E" });

  // Modal states
  const [showAddUser, setShowAddUser] = useState(false);
  const [showEditQuota, setShowEditQuota] = useState(null);
  const [showCreateOrg, setShowCreateOrg] = useState(null); // user_id for creating org
  const [showLinkOrg, setShowLinkOrg] = useState(null); // user_id for linking org
  const [showChangeRole, setShowChangeRole] = useState(null); // {user_id, org_id, current_role, org_name}
  const [showEditId, setShowEditId] = useState(null); // {type: 'structure'|'offer'|'option', item, org_id}
  const [showImpersonateConfirm, setShowImpersonateConfirm] = useState(null); // {userId, userName}
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserName, setNewUserName] = useState("");
  const [newQuota, setNewQuota] = useState(200);
  const [newOrgName, setNewOrgName] = useState("");
  const [selectedOrgId, setSelectedOrgId] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [newIdSuffix, setNewIdSuffix] = useState("");
  
  // Catalog management states
  const [selectedCatalogOrg, setSelectedCatalogOrg] = useState(null);
  const [catalogData, setCatalogData] = useState(null);
  const [loadingCatalog, setLoadingCatalog] = useState(false);

  useEffect(() => {
    checkSuperAdmin();
  }, []);

  const checkSuperAdmin = async () => {
    try {
      const response = await fetch(`${API}/admin/check`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setIsSuperAdmin(data.is_super_admin);
        if (data.is_super_admin) {
          loadAllData();
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadAllData = async () => {
    await Promise.all([
      fetchStats(),
      fetchOrganizations(),
      fetchUsers(),
      fetchApiKeys(),
      fetchBanner(),
    ]);
  };

  const fetchStats = async () => {
    try {
      const response = await fetch(`${API}/admin/stats`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setStats(data);
      }
    } catch (error) {
    }
  };

  const fetchOrganizations = async () => {
    try {
      const response = await fetch(`${API}/admin/organizations`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setOrganizations(data);
      }
    } catch (error) {
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await fetch(`${API}/admin/users`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (error) {
    }
  };

  const fetchApiKeys = async () => {
    try {
      const response = await fetch(`${API}/admin/api-keys`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
    }
  };

  const fetchBanner = async () => {
    try {
      const response = await fetch(`${API}/admin/banner`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setBanner(data);
      }
    } catch (error) {
    }
  };

  const fetchOrgCatalog = async (orgId) => {
    setLoadingCatalog(true);
    try {
      const response = await fetch(`${API}/admin/organizations/${orgId}/catalog`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setCatalogData(data);
        setSelectedCatalogOrg(orgId);
      }
    } catch (error) {
      toast.error("Erreur lors du chargement du catalogue");
    } finally {
      setLoadingCatalog(false);
    }
  };

  const handleUpdateId = async () => {
    if (!showEditId || !newIdSuffix.trim()) return;
    
    const { type, item } = showEditId;
    const endpoint = type === "structure" 
      ? `/admin/catalog/structure/${item.id}/id`
      : type === "offer" 
        ? `/admin/catalog/offer/${item.id}/id`
        : `/admin/catalog/option/${item.id}/id`;
    
    try {
      const response = await fetch(`${API}${endpoint}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ new_id_suffix: newIdSuffix.trim() }),
      });
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }
      
      const result = await response.json();
      toast.success(`ID modifié : ${result.old_id} → ${result.new_id}`);
      setShowEditId(null);
      setNewIdSuffix("");
      
      // Refresh catalog
      if (selectedCatalogOrg) {
        fetchOrgCatalog(selectedCatalogOrg);
      }
    } catch (error) {
      toast.error(error.message);
    }
  };

  const getIdPrefix = (type) => {
    switch (type) {
      case "structure": return "str_";
      case "offer": return "pack_";
      case "option": return "opt_";
      default: return "";
    }
  };

  const handleDeleteOrg = async (orgId, orgName) => {
    if (!window.confirm(`Supprimer l'organisation "${orgName}" et TOUTES ses données ? Cette action est irréversible.`)) return;

    try {
      const response = await fetch(`${API}/admin/organizations/${orgId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Erreur lors de la suppression");

      toast.success("Organisation supprimée");
      fetchOrganizations();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleSuspendUser = async (userId, currentStatus) => {
    const action = currentStatus ? "réactiver" : "suspendre";
    if (!window.confirm(`Voulez-vous ${action} cet utilisateur ?`)) return;

    try {
      const response = await fetch(`${API}/admin/users/${userId}/suspend`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ is_suspended: !currentStatus }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success(currentStatus ? "Utilisateur réactivé" : "Utilisateur suspendu");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Supprimer définitivement cet utilisateur ?")) return;

    try {
      const response = await fetch(`${API}/admin/users/${userId}`, {
        method: "DELETE",
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Utilisateur supprimé");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAddUser = async () => {
    if (!newUserEmail.trim()) {
      toast.error("Email requis");
      return;
    }

    try {
      const response = await fetch(`${API}/admin/users`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ email: newUserEmail, name: newUserName }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Utilisateur créé");
      setShowAddUser(false);
      setNewUserEmail("");
      setNewUserName("");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleToggleApiKey = async (keyId) => {
    try {
      const response = await fetch(`${API}/admin/api-keys/${keyId}/toggle`, {
        method: "PUT",
        credentials: "include",
      });

      if (!response.ok) throw new Error("Erreur");

      toast.success("Statut mis à jour");
      fetchApiKeys();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateQuota = async () => {
    if (!showEditQuota) return;

    try {
      const response = await fetch(`${API}/admin/api-keys/${showEditQuota}/quota`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ monthly_limit: newQuota }),
      });

      if (!response.ok) throw new Error("Erreur");

      toast.success("Quota mis à jour");
      setShowEditQuota(null);
      fetchApiKeys();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleUpdateBanner = async () => {
    try {
      const response = await fetch(`${API}/admin/banner`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify(banner),
      });

      if (!response.ok) throw new Error("Erreur");

      toast.success("Bannière mise à jour");
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleCreateOrgForUser = async () => {
    if (!showCreateOrg || !newOrgName.trim()) {
      toast.error("Nom de l'organisation requis");
      return;
    }

    try {
      const response = await fetch(`${API}/admin/users/${showCreateOrg}/create-organization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ name: newOrgName }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Organisation créée et liée à l'utilisateur");
      setShowCreateOrg(null);
      setNewOrgName("");
      fetchUsers();
      fetchOrganizations();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleLinkOrgToUser = async () => {
    if (!showLinkOrg || !selectedOrgId) {
      toast.error("Sélectionnez une organisation");
      return;
    }

    try {
      const response = await fetch(`${API}/admin/users/${showLinkOrg}/link-organization`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ org_id: selectedOrgId }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success("Utilisateur lié à l'organisation");
      setShowLinkOrg(null);
      setSelectedOrgId("");
      fetchUsers();
      fetchStats();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleChangeRole = async () => {
    if (!showChangeRole || !selectedRole) {
      toast.error("Sélectionnez un rôle");
      return;
    }

    try {
      const response = await fetch(`${API}/admin/users/${showChangeRole.user_id}/role`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ 
          org_id: showChangeRole.org_id,
          role: selectedRole 
        }),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      toast.success(`Rôle mis à jour: ${selectedRole}`);
      setShowChangeRole(null);
      setSelectedRole("");
      fetchUsers();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleImpersonate = async (userId, userName) => {
    setImpersonating(true);
    try {
      const response = await fetch(`${API}/admin/impersonate/${userId}`, {
        method: "POST",
        credentials: "include",
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail);
      }

      const data = await response.json();
      toast.success(data.message);
      
      // Refresh user context and redirect to dashboard
      if (refreshUser) {
        await refreshUser();
      }
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.message);
    } finally {
      setImpersonating(false);
      setShowImpersonateConfirm(null);
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case "owner":
        return <span className="text-xs px-2 py-0.5 rounded-full bg-purple-600 text-purple-100">Propriétaire</span>;
      case "admin":
        return <span className="text-xs px-2 py-0.5 rounded-full bg-blue-600 text-blue-100">Admin</span>;
      default:
        return <span className="text-xs px-2 py-0.5 rounded-full bg-gray-600 text-gray-200">Membre</span>;
    }
  };

  // Get users without organization
  const usersWithoutOrg = users.filter(u => !u.organizations || u.organizations.length === 0);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-purple-500 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!isSuperAdmin) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
        <Card className="max-w-md w-full bg-gray-800 border-gray-700">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-white mb-2">Accès refusé</h1>
            <p className="text-gray-400 mb-6">
              Cette page est réservée aux super administrateurs.
            </p>
            <Button onClick={() => navigate("/dashboard")} variant="outline">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Retour au dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Shield className="w-8 h-8 text-purple-500" />
            <div>
              <h1 className="text-xl font-bold">Super Admin</h1>
              <p className="text-sm text-gray-400">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              size="sm"
              variant="outline"
              onClick={loadAllData}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Actualiser
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => navigate("/dashboard")}
              className="border-gray-600 text-gray-300 hover:bg-gray-700"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              App
            </Button>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto p-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-gray-800 border border-gray-700 mb-6">
            <TabsTrigger value="stats" className="data-[state=active]:bg-purple-600">
              <BarChart3 className="w-4 h-4 mr-2" />
              Statistiques
            </TabsTrigger>
            <TabsTrigger value="organizations" className="data-[state=active]:bg-purple-600">
              <Building2 className="w-4 h-4 mr-2" />
              Organisations
            </TabsTrigger>
            <TabsTrigger value="users" className="data-[state=active]:bg-purple-600">
              <Users className="w-4 h-4 mr-2" />
              Utilisateurs
            </TabsTrigger>
            <TabsTrigger value="api-keys" className="data-[state=active]:bg-purple-600">
              <Key className="w-4 h-4 mr-2" />
              Clés API
            </TabsTrigger>
            <TabsTrigger value="banner" className="data-[state=active]:bg-purple-600">
              <Megaphone className="w-4 h-4 mr-2" />
              Bannière
            </TabsTrigger>
            <TabsTrigger value="catalog" className="data-[state=active]:bg-purple-600">
              <Package className="w-4 h-4 mr-2" />
              Catalogue IDs
            </TabsTrigger>
          </TabsList>

          {/* Stats Tab */}
          <TabsContent value="stats">
            {stats && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">Organisations</p>
                        <p className="text-3xl font-bold text-white">{stats.total_organizations}</p>
                      </div>
                      <Building2 className="w-10 h-10 text-purple-500" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">Utilisateurs</p>
                        <p className="text-3xl font-bold text-white">{stats.total_users}</p>
                        {stats.suspended_users > 0 && (
                          <p className="text-xs text-red-400">{stats.suspended_users} suspendu(s)</p>
                        )}
                      </div>
                      <Users className="w-10 h-10 text-blue-500" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">Réservations</p>
                        <p className="text-3xl font-bold text-white">{stats.total_reservations}</p>
                      </div>
                      <Calendar className="w-10 h-10 text-green-500" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-gray-400 text-sm">Appels API ce mois</p>
                        <p className="text-3xl font-bold text-white">{stats.api_calls_this_month}</p>
                        <p className="text-xs text-gray-400">{stats.active_api_keys} clés actives</p>
                      </div>
                      <Key className="w-10 h-10 text-amber-500" />
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-gray-800 border-gray-700 md:col-span-2">
                  <CardHeader>
                    <CardTitle className="text-white">Réservations par statut</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-4 gap-4">
                      <div className="text-center p-3 bg-amber-500/20 rounded-lg">
                        <p className="text-2xl font-bold text-amber-400">{stats.reservations_by_status?.OPTION || 0}</p>
                        <p className="text-xs text-gray-400">OPTIONS</p>
                      </div>
                      <div className="text-center p-3 bg-green-500/20 rounded-lg">
                        <p className="text-2xl font-bold text-green-400">{stats.reservations_by_status?.CONFIRME || 0}</p>
                        <p className="text-xs text-gray-400">CONFIRMÉES</p>
                      </div>
                      <div className="text-center p-3 bg-red-500/20 rounded-lg">
                        <p className="text-2xl font-bold text-red-400">{stats.reservations_by_status?.ANNULE || 0}</p>
                        <p className="text-xs text-gray-400">ANNULÉES</p>
                      </div>
                      <div className="text-center p-3 bg-gray-500/20 rounded-lg">
                        <p className="text-2xl font-bold text-gray-400">{stats.reservations_by_status?.BLOQUE || 0}</p>
                        <p className="text-xs text-gray-400">BLOQUÉES</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Organizations Tab */}
          <TabsContent value="organizations">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Building2 className="w-5 h-5" />
                  Toutes les organisations ({organizations.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {organizations.map((org) => (
                    <div
                      key={org.id}
                      className="p-4 bg-gray-700/50 rounded-lg flex items-center justify-between"
                    >
                      <div>
                        <p className="font-semibold text-white">{org.name}</p>
                        <p className="text-sm text-gray-400">
                          {org.member_count} membre(s) • {org.reservation_count} réservation(s) • {org.api_key_count} clé(s) API
                        </p>
                        <p className="text-xs text-gray-500 mt-1">ID: {org.id}</p>
                      </div>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDeleteOrg(org.id, org.name)}
                        data-testid={`delete-org-${org.id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  {organizations.length === 0 && (
                    <p className="text-center text-gray-400 py-8">Aucune organisation</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center justify-between">
                  <span className="flex items-center gap-2">
                    <Users className="w-5 h-5" />
                    Tous les utilisateurs ({users.length})
                  </span>
                  <Button
                    size="sm"
                    onClick={() => setShowAddUser(true)}
                    className="bg-purple-600 hover:bg-purple-700"
                    data-testid="add-user-btn"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Ajouter
                  </Button>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {users.map((u) => (
                    <div
                      key={u.user_id}
                      className={`p-4 rounded-lg flex items-center justify-between ${
                        u.is_suspended ? "bg-red-900/20 border border-red-800" : "bg-gray-700/50"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {u.picture ? (
                          <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                        ) : (
                          <div className="w-10 h-10 rounded-full bg-gray-600 flex items-center justify-center">
                            <Users className="w-5 h-5 text-gray-400" />
                          </div>
                        )}
                        <div>
                          <p className="font-semibold text-white flex items-center gap-2">
                            {u.name || "Sans nom"}
                            {u.is_suspended && (
                              <span className="text-xs px-2 py-0.5 bg-red-600 rounded-full">Suspendu</span>
                            )}
                            {u.email === "glowny.company@gmail.com" && (
                              <span className="text-xs px-2 py-0.5 bg-purple-600 rounded-full">Super Admin</span>
                            )}
                          </p>
                          <p className="text-sm text-gray-400">{u.email}</p>
                          <div className="flex flex-wrap items-center gap-2 mt-1">
                            {u.organizations?.length > 0 
                              ? u.organizations.map(o => (
                                  <span key={o.id} className="flex items-center gap-1">
                                    <span className="text-xs text-gray-400">{o.name}</span>
                                    {getRoleBadge(o.role)}
                                    <button
                                      onClick={() => {
                                        setShowChangeRole({
                                          user_id: u.user_id,
                                          org_id: o.id,
                                          current_role: o.role,
                                          org_name: o.name,
                                          user_name: u.name
                                        });
                                        setSelectedRole(o.role);
                                      }}
                                      className="text-gray-500 hover:text-purple-400 ml-1"
                                      title="Modifier le rôle"
                                    >
                                      <Pencil className="w-3 h-3" />
                                    </button>
                                  </span>
                                ))
                              : <span className="text-amber-400 text-xs">⚠️ Aucune organisation</span>}
                          </div>
                        </div>
                      </div>
                      {u.email !== "glowny.company@gmail.com" && (
                        <div className="flex items-center gap-2">
                          {/* Impersonate button - always visible for non-super-admin users */}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setShowImpersonateConfirm({ userId: u.user_id, userName: u.name || u.email })}
                            disabled={impersonating}
                            className="text-purple-400 border-purple-600 hover:bg-purple-600/20"
                            data-testid={`impersonate-${u.user_id}`}
                            title="Se connecter en tant que cet utilisateur"
                          >
                            <LogIn className="w-4 h-4 mr-1" />
                            Accéder
                          </Button>
                          {/* Buttons for users without organization */}
                          {(!u.organizations || u.organizations.length === 0) && (
                            <>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setShowCreateOrg(u.user_id);
                                  setNewOrgName("");
                                }}
                                className="text-green-400 border-green-600 hover:bg-green-600/20"
                                data-testid={`create-org-${u.user_id}`}
                              >
                                <Plus className="w-4 h-4 mr-1" />
                                Créer org
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setShowLinkOrg(u.user_id);
                                  setSelectedOrgId("");
                                }}
                                className="text-blue-400 border-blue-600 hover:bg-blue-600/20"
                                data-testid={`link-org-${u.user_id}`}
                              >
                                <Building2 className="w-4 h-4 mr-1" />
                                Lier
                              </Button>
                            </>
                          )}
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleSuspendUser(u.user_id, u.is_suspended)}
                            className={u.is_suspended ? "text-green-500 hover:text-green-400" : "text-amber-500 hover:text-amber-400"}
                            data-testid={`suspend-user-${u.user_id}`}
                          >
                            {u.is_suspended ? <CheckCircle className="w-5 h-5" /> : <Ban className="w-5 h-5" />}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => handleDeleteUser(u.user_id)}
                            className="text-red-500 hover:text-red-400"
                            data-testid={`delete-user-${u.user_id}`}
                          >
                            <Trash2 className="w-5 h-5" />
                          </Button>
                        </div>
                      )}
                    </div>
                  ))}
                  {users.length === 0 && (
                    <p className="text-center text-gray-400 py-8">Aucun utilisateur</p>
                  )}
                </div>

                {/* Section for users without organization */}
                {usersWithoutOrg.length > 0 && (
                  <div className="mt-6 p-4 bg-amber-900/20 border border-amber-700 rounded-lg">
                    <p className="text-amber-400 font-semibold flex items-center gap-2 mb-2">
                      <AlertTriangle className="w-5 h-5" />
                      {usersWithoutOrg.length} utilisateur(s) sans organisation
                    </p>
                    <p className="text-sm text-gray-400">
                      Ces utilisateurs se sont connectés mais n'ont pas terminé l'onboarding.
                      Vous pouvez créer une organisation pour eux ou les lier à une organisation existante.
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* API Keys Tab */}
          <TabsContent value="api-keys">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Key className="w-5 h-5" />
                  Toutes les clés API ({apiKeys.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {apiKeys.map((key) => (
                    <div
                      key={key.id}
                      className={`p-4 rounded-lg flex items-center justify-between ${
                        key.is_active ? "bg-gray-700/50" : "bg-gray-700/20 opacity-60"
                      }`}
                    >
                      <div>
                        <p className="font-semibold text-white flex items-center gap-2">
                          {key.name}
                          {!key.is_active && (
                            <span className="text-xs px-2 py-0.5 bg-gray-600 rounded-full">Désactivée</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-400">{key.org_name}</p>
                        <p className="text-xs text-gray-500 font-mono">{key.key_prefix}...</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {key.calls_this_month} / {key.monthly_limit} appels ce mois
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setShowEditQuota(key.id);
                            setNewQuota(key.monthly_limit);
                          }}
                          className="border-gray-600 text-gray-300"
                          data-testid={`edit-quota-${key.id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Quota
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleToggleApiKey(key.id)}
                          className={key.is_active ? "text-green-500" : "text-gray-500"}
                          data-testid={`toggle-admin-key-${key.id}`}
                        >
                          <Power className="w-5 h-5" />
                        </Button>
                      </div>
                    </div>
                  ))}
                  {apiKeys.length === 0 && (
                    <p className="text-center text-gray-400 py-8">Aucune clé API</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Banner Tab */}
          <TabsContent value="banner">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Megaphone className="w-5 h-5" />
                  Bannière utilisateurs
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label className="text-gray-300">Message de la bannière</Label>
                  <Textarea
                    value={banner.message}
                    onChange={(e) => setBanner({ ...banner, message: e.target.value })}
                    placeholder="Ex: Maintenance prévue le 15 janvier de 10h à 12h"
                    className="mt-1 bg-gray-700 border-gray-600 text-white"
                    rows={3}
                    data-testid="banner-message-input"
                  />
                </div>

                <div className="flex items-center gap-4">
                  <div className="flex-1">
                    <Label className="text-gray-300">Couleur de fond</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={banner.bg_color}
                        onChange={(e) => setBanner({ ...banner, bg_color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={banner.bg_color}
                        onChange={(e) => setBanner({ ...banner, bg_color: e.target.value })}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                  </div>
                  <div className="flex-1">
                    <Label className="text-gray-300">Couleur du texte</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <input
                        type="color"
                        value={banner.text_color}
                        onChange={(e) => setBanner({ ...banner, text_color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer"
                      />
                      <Input
                        value={banner.text_color}
                        onChange={(e) => setBanner({ ...banner, text_color: e.target.value })}
                        className="bg-gray-700 border-gray-600 text-white"
                      />
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <input
                    type="checkbox"
                    id="bannerActive"
                    checked={banner.is_active}
                    onChange={(e) => setBanner({ ...banner, is_active: e.target.checked })}
                    className="w-5 h-5"
                    data-testid="banner-active-checkbox"
                  />
                  <Label htmlFor="bannerActive" className="text-gray-300 cursor-pointer">
                    Activer la bannière
                  </Label>
                </div>

                {/* Preview */}
                {banner.message && (
                  <div className="mt-4">
                    <Label className="text-gray-300 mb-2 block">Aperçu :</Label>
                    <div
                      className="p-3 rounded-lg text-center text-sm"
                      style={{ backgroundColor: banner.bg_color, color: banner.text_color }}
                    >
                      <AlertTriangle className="w-4 h-4 inline mr-2" />
                      {banner.message}
                    </div>
                  </div>
                )}

                <Button
                  onClick={handleUpdateBanner}
                  className="w-full bg-purple-600 hover:bg-purple-700"
                  data-testid="save-banner-btn"
                >
                  Enregistrer la bannière
                </Button>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Catalog IDs Tab */}
          <TabsContent value="catalog">
            <Card className="bg-gray-800 border-gray-700">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Settings2 className="w-5 h-5 text-purple-400" />
                  Gestion des identifiants du catalogue
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Organization selector */}
                <div>
                  <Label className="text-gray-300">Sélectionner une organisation</Label>
                  <select
                    className="w-full mt-1 p-2 rounded bg-gray-700 border border-gray-600 text-white"
                    value={selectedCatalogOrg || ""}
                    onChange={(e) => e.target.value && fetchOrgCatalog(e.target.value)}
                    data-testid="catalog-org-select"
                  >
                    <option value="">-- Choisir une organisation --</option>
                    {organizations.map((org) => (
                      <option key={org.id} value={org.id}>{org.name}</option>
                    ))}
                  </select>
                </div>

                {loadingCatalog && (
                  <div className="text-center py-8">
                    <div className="w-8 h-8 border-2 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto" />
                    <p className="text-gray-400 mt-2">Chargement...</p>
                  </div>
                )}

                {catalogData && !loadingCatalog && (
                  <div className="space-y-6">
                    {/* Structures */}
                    <div>
                      <h3 className="text-lg font-semibold text-cyan-400 mb-3 flex items-center gap-2">
                        <Package className="w-5 h-5" />
                        Structures ({catalogData.structures?.length || 0})
                      </h3>
                      <div className="space-y-2">
                        {catalogData.structures?.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                            <div>
                              <span className="text-white font-medium">{item.name}</span>
                              <div className="text-xs text-gray-400 font-mono mt-1">ID: {item.id}</div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-cyan-500 text-cyan-400 hover:bg-cyan-500/20"
                              onClick={() => {
                                setShowEditId({ type: "structure", item, org_id: catalogData.organization.id });
                                setNewIdSuffix(item.id.replace("str_", "").replace(/^org_[^_]+_/, ""));
                              }}
                              data-testid={`edit-structure-id-${item.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Modifier ID
                            </Button>
                          </div>
                        ))}
                        {(!catalogData.structures || catalogData.structures.length === 0) && (
                          <p className="text-gray-500 text-sm">Aucune structure</p>
                        )}
                      </div>
                    </div>

                    {/* Offers */}
                    <div>
                      <h3 className="text-lg font-semibold text-green-400 mb-3 flex items-center gap-2">
                        <Tag className="w-5 h-5" />
                        Offres / Packs ({catalogData.offers?.length || 0})
                      </h3>
                      <div className="space-y-2">
                        {catalogData.offers?.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                            <div>
                              <span className="text-white font-medium">{item.name}</span>
                              <span className="text-green-400 ml-2">{item.price}€</span>
                              <div className="text-xs text-gray-400 font-mono mt-1">ID: {item.id}</div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-green-500 text-green-400 hover:bg-green-500/20"
                              onClick={() => {
                                setShowEditId({ type: "offer", item, org_id: catalogData.organization.id });
                                setNewIdSuffix(item.id.replace("pack_", "").replace(/^org_[^_]+_/, ""));
                              }}
                              data-testid={`edit-offer-id-${item.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Modifier ID
                            </Button>
                          </div>
                        ))}
                        {(!catalogData.offers || catalogData.offers.length === 0) && (
                          <p className="text-gray-500 text-sm">Aucune offre</p>
                        )}
                      </div>
                    </div>

                    {/* Options */}
                    <div>
                      <h3 className="text-lg font-semibold text-amber-400 mb-3 flex items-center gap-2">
                        <Plus className="w-5 h-5" />
                        Options ({catalogData.options?.length || 0})
                      </h3>
                      <div className="space-y-2">
                        {catalogData.options?.map((item) => (
                          <div key={item.id} className="flex items-center justify-between p-3 bg-gray-700 rounded-lg">
                            <div>
                              <span className="text-white font-medium">{item.name}</span>
                              <span className="text-amber-400 ml-2">{item.price}€</span>
                              <div className="text-xs text-gray-400 font-mono mt-1">ID: {item.id}</div>
                            </div>
                            <Button
                              size="sm"
                              variant="outline"
                              className="border-amber-500 text-amber-400 hover:bg-amber-500/20"
                              onClick={() => {
                                setShowEditId({ type: "option", item, org_id: catalogData.organization.id });
                                setNewIdSuffix(item.id.replace("opt_", "").replace(/^org_[^_]+_/, ""));
                              }}
                              data-testid={`edit-option-id-${item.id}`}
                            >
                              <Pencil className="w-4 h-4 mr-1" />
                              Modifier ID
                            </Button>
                          </div>
                        ))}
                        {(!catalogData.options || catalogData.options.length === 0) && (
                          <p className="text-gray-500 text-sm">Aucune option</p>
                        )}
                      </div>
                    </div>

                    {/* Info box */}
                    <div className="p-4 bg-purple-900/30 border border-purple-500/30 rounded-lg">
                      <h4 className="text-purple-300 font-medium mb-2">Format des identifiants</h4>
                      <ul className="text-sm text-gray-300 space-y-1">
                        <li>• <span className="text-cyan-400">Structures</span> : str_<span className="text-gray-400">[votre-suffixe]</span></li>
                        <li>• <span className="text-green-400">Offres</span> : pack_<span className="text-gray-400">[votre-suffixe]</span></li>
                        <li>• <span className="text-amber-400">Options</span> : opt_<span className="text-gray-400">[votre-suffixe]</span></li>
                      </ul>
                      <p className="text-xs text-gray-400 mt-2">
                        Les réservations existantes seront automatiquement mises à jour.
                      </p>
                    </div>
                  </div>
                )}

                {!catalogData && !loadingCatalog && (
                  <div className="text-center py-12 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                    <p>Sélectionnez une organisation pour voir son catalogue</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Add User Dialog */}
      <Dialog open={showAddUser} onOpenChange={setShowAddUser}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Ajouter un utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Email</Label>
              <Input
                type="email"
                value={newUserEmail}
                onChange={(e) => setNewUserEmail(e.target.value)}
                className="mt-1 bg-gray-700 border-gray-600"
                placeholder="email@exemple.com"
                data-testid="new-user-email-input"
              />
            </div>
            <div>
              <Label>Nom (optionnel)</Label>
              <Input
                value={newUserName}
                onChange={(e) => setNewUserName(e.target.value)}
                className="mt-1 bg-gray-700 border-gray-600"
                placeholder="Jean Dupont"
                data-testid="new-user-name-input"
              />
            </div>
            <Button
              onClick={handleAddUser}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-add-user-btn"
            >
              Créer l'utilisateur
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Quota Dialog */}
      <Dialog open={!!showEditQuota} onOpenChange={() => setShowEditQuota(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Modifier le quota mensuel</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Limite d'appels par mois</Label>
              <Input
                type="number"
                value={newQuota}
                onChange={(e) => setNewQuota(parseInt(e.target.value) || 0)}
                className="mt-1 bg-gray-700 border-gray-600"
                min={0}
                data-testid="quota-input"
              />
            </div>
            <Button
              onClick={handleUpdateQuota}
              className="w-full bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-quota-btn"
            >
              Enregistrer
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Organization Dialog */}
      <Dialog open={!!showCreateOrg} onOpenChange={() => setShowCreateOrg(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-green-400" />
              Créer une organisation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Une nouvelle organisation sera créée et l'utilisateur en sera le propriétaire.
            </p>
            <div>
              <Label>Nom de l'organisation</Label>
              <Input
                value={newOrgName}
                onChange={(e) => setNewOrgName(e.target.value)}
                className="mt-1 bg-gray-700 border-gray-600"
                placeholder="Ex: Mon Entreprise"
                data-testid="new-org-name-input"
              />
            </div>
            <Button
              onClick={handleCreateOrgForUser}
              className="w-full bg-green-600 hover:bg-green-700"
              data-testid="confirm-create-org-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Créer l'organisation
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Link Organization Dialog */}
      <Dialog open={!!showLinkOrg} onOpenChange={() => setShowLinkOrg(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 className="w-5 h-5 text-blue-400" />
              Lier à une organisation
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Sélectionnez une organisation existante. L'utilisateur sera ajouté en tant que membre.
            </p>
            <div>
              <Label>Organisation</Label>
              <select
                value={selectedOrgId}
                onChange={(e) => setSelectedOrgId(e.target.value)}
                className="w-full mt-1 p-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                data-testid="select-org-dropdown"
              >
                <option value="">-- Sélectionnez --</option>
                {organizations.map((org) => (
                  <option key={org.id} value={org.id}>
                    {org.name} ({org.member_count} membre(s))
                  </option>
                ))}
              </select>
            </div>
            <Button
              onClick={handleLinkOrgToUser}
              disabled={!selectedOrgId}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
              data-testid="confirm-link-org-btn"
            >
              <Building2 className="w-4 h-4 mr-2" />
              Lier l'utilisateur
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Change Role Dialog */}
      <Dialog open={!!showChangeRole} onOpenChange={() => setShowChangeRole(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="w-5 h-5 text-purple-400" />
              Modifier le rôle
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-400">
              Modifier le rôle de <span className="text-white font-medium">{showChangeRole?.user_name}</span> dans l'organisation <span className="text-white font-medium">{showChangeRole?.org_name}</span>
            </p>
            <div>
              <Label>Rôle</Label>
              <select
                value={selectedRole}
                onChange={(e) => setSelectedRole(e.target.value)}
                className="w-full mt-1 p-2 bg-gray-700 border border-gray-600 rounded-md text-white"
                data-testid="select-role-dropdown"
              >
                <option value="member">👤 Membre - Accès en lecture</option>
                <option value="admin">🛠️ Admin - Gestion complète</option>
                <option value="owner">👑 Propriétaire - Tous les droits</option>
              </select>
            </div>
            <div className="p-3 bg-gray-700/50 rounded-lg text-xs text-gray-400">
              <p><strong className="text-purple-400">Propriétaire :</strong> Tous les droits, gestion des membres</p>
              <p><strong className="text-blue-400">Admin :</strong> Créer/modifier réservations, catalogue, paramètres</p>
              <p><strong className="text-gray-300">Membre :</strong> Voir les réservations et le planning</p>
            </div>
            <Button
              onClick={handleChangeRole}
              disabled={!selectedRole || selectedRole === showChangeRole?.current_role}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
              data-testid="confirm-change-role-btn"
            >
              <UserCog className="w-4 h-4 mr-2" />
              Modifier le rôle
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit ID Dialog */}
      <Dialog open={!!showEditId} onOpenChange={() => { setShowEditId(null); setNewIdSuffix(""); }}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle>Modifier l'identifiant</DialogTitle>
          </DialogHeader>
          {showEditId && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-700 rounded-lg">
                <p className="text-sm text-gray-400">Élément</p>
                <p className="text-white font-medium">{showEditId.item?.name}</p>
                <p className="text-xs text-gray-500 font-mono mt-1">ID actuel: {showEditId.item?.id}</p>
              </div>
              
              <div>
                <Label className="text-gray-300">Nouveau suffixe</Label>
                <div className="flex items-center mt-1">
                  <span className="px-3 py-2 bg-gray-600 border border-gray-500 rounded-l text-gray-300 text-sm">
                    {getIdPrefix(showEditId.type)}
                  </span>
                  <Input
                    value={newIdSuffix}
                    onChange={(e) => setNewIdSuffix(e.target.value.toLowerCase().replace(/[^a-z0-9\-]/g, ''))}
                    className="bg-gray-700 border-gray-600 rounded-l-none"
                    placeholder="mon-identifiant"
                    data-testid="new-id-suffix-input"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Lettres minuscules, chiffres et tirets uniquement
                </p>
              </div>
              
              {newIdSuffix && (
                <div className="p-3 bg-purple-900/30 border border-purple-500/30 rounded-lg">
                  <p className="text-sm text-gray-400">Nouvel ID :</p>
                  <p className="text-purple-300 font-mono">{getIdPrefix(showEditId.type)}{newIdSuffix}</p>
                </div>
              )}
              
              <Button
                onClick={handleUpdateId}
                disabled={!newIdSuffix || newIdSuffix.length < 2}
                className="w-full bg-purple-600 hover:bg-purple-700 disabled:opacity-50"
                data-testid="confirm-update-id-btn"
              >
                Mettre à jour l'identifiant
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Impersonate Confirmation Dialog */}
      <Dialog open={!!showImpersonateConfirm} onOpenChange={() => setShowImpersonateConfirm(null)}>
        <DialogContent className="bg-gray-800 border-gray-700 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <LogIn className="w-5 h-5 text-purple-400" />
              Confirmer l'accès
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-gray-300">
              Vous allez vous connecter en tant que <span className="font-semibold text-white">"{showImpersonateConfirm?.userName}"</span>.
            </p>
            <p className="text-sm text-gray-400">
              Vous pourrez revenir à votre compte Super Admin à tout moment via la bannière en haut de l'écran.
            </p>
          </div>
          <div className="flex gap-3 justify-end">
            <Button
              variant="outline"
              onClick={() => setShowImpersonateConfirm(null)}
              className="border-gray-600"
            >
              Annuler
            </Button>
            <Button
              onClick={() => handleImpersonate(showImpersonateConfirm?.userId, showImpersonateConfirm?.userName)}
              disabled={impersonating}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="confirm-impersonate-btn"
            >
              {impersonating ? "Connexion..." : "Continuer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
